import { Outlet, Link, useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import '../App.css';
import { SiteHeader, SiteFooter, SectionLabel } from '../sections/site-chrome.js';
import { getPostsByType } from './posts/index.js';
import { HeadingsProvider, useHeadings } from './headings-context.js';
import theme from '../theme';

const t = theme;

const HEADER_HEIGHT = 64;
const ARTICLE_MAX_WIDTH = '1200px';

// Sticky side column used by both the post list and the table of contents
const stickyColumn = {
  position: 'sticky',
  top: HEADER_HEIGHT + 32,
  alignSelf: 'start',
  maxHeight: `calc(100vh - ${HEADER_HEIGHT + 64}px)`,
  overflowY: 'auto',
  mt: 8,
  scrollbarWidth: 'none',
  '&::-webkit-scrollbar': { display: 'none' }
};

function SidebarPostItem({ post, currentSlug }) {
  const active = currentSlug === post.slug;

  return (
    <Box
      component={Link}
      to={`/writings/${post.slug}`}
      sx={{
        display: 'block',
        py: 1,
        pl: 1.5,
        ml: '-1px',
        borderLeft: `1px solid ${active ? t.colors.textPrimary : 'transparent'}`,
        fontSize: t.typography.fontSize.sm,
        lineHeight: t.typography.lineHeight.normal,
        fontWeight: active ? t.typography.fontWeight.medium : t.typography.fontWeight.regular,
        color: active ? t.colors.textPrimary : t.colors.textSecondary,
        textDecoration: 'none',
        transition: t.transitions.fast,
        '&:hover': { color: t.colors.textPrimary }
      }}
    >
      {post.title}
    </Box>
  );
}

function SidebarSection({ label, posts, currentSlug }) {
  if (posts.length === 0) return null;

  return (
    <Box sx={{ mb: 4 }}>
      <SectionLabel>{label}</SectionLabel>
      <Box sx={{ borderLeft: `1px solid ${t.colors.border}`, mt: -1.5 }}>
        {posts.map((post) => (
          <SidebarPostItem key={post.slug} post={post} currentSlug={currentSlug} />
        ))}
      </Box>
    </Box>
  );
}

function ArticleSidebar({ currentSlug }) {
  return (
    <Box component="aside" sx={{ ...stickyColumn, display: { xs: 'none', md: 'block' } }}>
      <SidebarSection label="Posts" posts={getPostsByType('post')} currentSlug={currentSlug} />
      <SidebarSection
        label="Case studies"
        posts={getPostsByType('case-study')}
        currentSlug={currentSlug}
      />
    </Box>
  );
}

// Table of contents: h2 sections with their h3s indented below
function TableOfContents() {
  const { headings, activeId } = useHeadings();

  // The h1 is the article title, so only list the sections below it
  const items = headings.filter((heading) => heading.level > 1);
  if (items.length === 0) return null;

  const handleClick = (e, id) => {
    e.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <Box component="nav" sx={{ ...stickyColumn, display: { xs: 'none', lg: 'block' } }}>
      <SectionLabel>On this page</SectionLabel>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, mt: -1.5 }}>
        {items.map((heading) => {
          const active = activeId === heading.id;
          return (
            <Box
              key={heading.id}
              component="a"
              href={`#${heading.id}`}
              onClick={(e) => handleClick(e, heading.id)}
              sx={{
                py: 0.5,
                pl: heading.level === 3 ? 1.5 : 0,
                fontSize: heading.level === 3 ? t.typography.fontSize.xs : t.typography.fontSize.sm,
                lineHeight: t.typography.lineHeight.normal,
                color: active ? t.colors.textPrimary : t.colors.textMuted,
                fontWeight: active ? t.typography.fontWeight.medium : t.typography.fontWeight.regular,
                textDecoration: 'none',
                transition: t.transitions.fast,
                '&:hover': { color: t.colors.textPrimary }
              }}
            >
              {heading.text}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

function ArticleViewLayout({ currentSlug }) {
  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: t.colors.background }}>
      <SiteHeader maxWidth={ARTICLE_MAX_WIDTH} />
      <Box
        sx={{
          maxWidth: ARTICLE_MAX_WIDTH,
          mx: 'auto',
          px: { xs: 2, md: 3 },
          display: 'grid',
          gridTemplateColumns: {
            xs: 'minmax(0, 1fr)',
            md: '200px minmax(0, 1fr)',
            lg: '200px minmax(0, 1fr) 200px'
          },
          columnGap: { md: 6, lg: 8 }
        }}
      >
        <ArticleSidebar currentSlug={currentSlug} />
        <Box component="main" sx={{ minWidth: 0, pt: { xs: 5, md: 8 } }}>
          <Outlet />
        </Box>
        <TableOfContents />
      </Box>
      <Box sx={{ maxWidth: ARTICLE_MAX_WIDTH, mx: 'auto', px: { xs: 2, md: 3 } }}>
        <SiteFooter />
      </Box>
    </Box>
  );
}

function WritingsLayout() {
  const location = useLocation();

  // Check if we're viewing an article by looking at the URL path
  const pathParts = location.pathname.split('/').filter(Boolean);
  const isArticleView = pathParts.length > 1 && pathParts[0] === 'writings';
  const currentSlug = isArticleView ? pathParts[1] : null;

  // If we're at /writings (no slug), show the full article list
  if (!isArticleView) {
    return (
      <Box sx={{ minHeight: '100vh', backgroundColor: t.colors.background }}>
        <SiteHeader />
        <Box
          component="main"
          sx={{ maxWidth: t.layout.contentWidth, mx: 'auto', px: { xs: 2, md: 3 } }}
        >
          <Outlet />
          <SiteFooter />
        </Box>
      </Box>
    );
  }

  // If we're viewing an article, wrap with HeadingsProvider
  return (
    <HeadingsProvider>
      <ArticleViewLayout currentSlug={currentSlug} />
    </HeadingsProvider>
  );
}

export default WritingsLayout;
